import { FOCUS_RING, HOVER_T } from "@/components/rail-kit/registers";
import { cn } from "@/utils";
import type { LucideIcon } from "lucide-react";

/**
 * ActionRow — the quiet text-link register: a list's own closing affordance
 * (Show more, New chat, Add connector, Archive), never a boxed button. Muted
 * at rest, brightens on hover; same pitch as content rows.
 */
export function ActionRow({
  icon: Icon,
  label,
  onClick,
  className,
  align = "head",
}: {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  className?: string;
  /**
   * What the verb sits under. "head" (top-level runs) slots the glyph in a
   * head-tile-width box so the label lines up with head labels; "row"
   * (inside a section's content) uses the content rows' own icon slot so
   * the ＋ aligns with the row icons above and the label with their labels —
   * a step quieter in size, per the DS closing-verb register.
   */
  align?: "head" | "row";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex min-h-7 w-full items-center gap-2.5 rounded-md px-2 text-left text-n-50 hover:bg-n-05 hover:text-n-70",
        HOVER_T,
        FOCUS_RING,
        className,
      )}
    >
      {/* No coin either way: this is the quiet text-link register, not a
          head — the slot width alone decides which column it anchors to. */}
      <span
        className={cn(
          "flex shrink-0 items-center justify-center",
          align === "head" ? "size-5" : "size-4",
        )}
      >
        <Icon className="size-3.5" />
      </span>
      <span className={align === "head" ? "text-base" : "text-sm"}>
        {label}
      </span>
    </button>
  );
}
